import React, { useState } from "react";
import { X, Check, Copy, Link, ShareNetwork, Eye, ArrowRight } from "phosphor-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";

const SECTION_OPTIONS = [
  { id: "imported_global_performance", label: "Global Performance", description: "Win rate, RR, profit factor" },
  { id: "imported_setup_performance", label: "Setup Performance", description: "Analytics by trading setup" },
  { id: "imported_timeframe_performance", label: "Timeframe Performance", description: "Analytics by timeframe" },
];

export default function ImportedShareModal({ 
  isOpen, 
  onClose,
  // Context props
  selectedBatch = 'all',
  selectedBatchName = null,
  compareMode = false,
  selectedBatchIds = [],
  batches = []
}) {
  const [step, setStep] = useState(1); // 1: configure, 2: success
  const [creating, setCreating] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Form state
  const [title, setTitle] = useState(() => {
    if (compareMode && selectedBatchIds.length >= 2) {
      return "Imported Batch Comparison";
    } else if (selectedBatch !== 'all' && selectedBatchName) {
      return `${selectedBatchName} - Imported Analytics`;
    }
    return "Imported Analytics Report";
  });
  const [description, setDescription] = useState('');
  const [sections, setSections] = useState({
    imported_global_performance: true,
    imported_setup_performance: true,
    imported_timeframe_performance: true,
    batch_comparison: compareMode && selectedBatchIds.length >= 2
  });

  const toggleSection = (sectionId) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleCreateShare = async () => {
    setCreating(true);
    try {
      // Build imported context
      const importedContext = {
        batch_id: selectedBatch,
        batch_ids: compareMode ? selectedBatchIds : [],
        compare_mode: compareMode
      };
      
      const response = await api.post('/shares', {
        title: title || "Imported Analytics Report",
        description: description || null,
        share_type: "imported_analytics",
        sections,
        imported_context: importedContext,
        time_range: "all"
      });
      
      const shareId = response.data.id;
      const url = `${window.location.origin}/share/${shareId}`;
      setShareUrl(url);
      setStep(2);
      toast.success("Share link created!");
    } catch (error) {
      console.error("Error creating share:", error);
      toast.error("Failed to create share link");
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const handleClose = () => {
    setStep(1);
    setShareUrl('');
    setCopied(false);
    onClose();
  };

  // Get context description
  const getContextDescription = () => {
    if (compareMode && selectedBatchIds.length >= 2) {
      const names = selectedBatchIds
        .map(id => batches.find(b => b.batch_id === id)?.import_name)
        .filter(Boolean)
        .join(', ');
      return `Comparing: ${names}`;
    } else if (selectedBatch !== 'all' && selectedBatchName) {
      return `Sharing: ${selectedBatchName}`;
    }
    return "Sharing: All Imports (aggregated)";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={handleClose}>
      <div 
        className="bg-[#0A0A0A] border border-white/10 rounded-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#00E676]/10 rounded-full flex items-center justify-center">
              <ShareNetwork size={20} className="text-[#00E676]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {step === 1 ? 'Share Imported Analytics' : 'Share Link Created'}
              </h2>
              <p className="text-white/40 text-sm">{getContextDescription()}</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-white/40 hover:text-white transition-colors">
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              {/* Context info */}
              <div className="bg-[#00B8D4]/5 border border-[#00B8D4]/20 rounded-sm p-4">
                <p className="text-[#00B8D4] text-sm">
                  {compareMode && selectedBatchIds.length >= 2 
                    ? "You are sharing a comparison between selected import batches."
                    : selectedBatch !== 'all' 
                      ? "You are sharing this imported batch only."
                      : "You are sharing aggregated Imported Analytics data."
                  }
                </p>
              </div>
              
              {/* Title */}
              <div>
                <label className="text-white/60 text-sm mb-2 block">Report Title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter a title..."
                  className="bg-[#121212] border-white/10 text-white"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="text-white/60 text-sm mb-2 block">Description (optional)</label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description..."
                  className="bg-[#121212] border-white/10 text-white min-h-[80px]"
                />
              </div>
              
              {/* Sections */}
              <div>
                <label className="text-white/60 text-sm mb-3 block">Include Sections</label>
                <div className="space-y-2">
                  {SECTION_OPTIONS.map(option => (
                    <button
                      key={option.id}
                      onClick={() => toggleSection(option.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-sm border transition-colors ${
                        sections[option.id]
                          ? 'border-[#00E676]/30 bg-[#00E676]/5'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className="text-left">
                        <p className={sections[option.id] ? 'text-[#00E676]' : 'text-white/80'}>
                          {option.label}
                        </p>
                        <p className="text-white/40 text-xs">{option.description}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-sm border flex items-center justify-center ${
                        sections[option.id] 
                          ? 'bg-[#00E676] border-[#00E676]' 
                          : 'border-white/30'
                      }`}>
                        {sections[option.id] && <Check size={12} weight="bold" className="text-black" />}
                      </div>
                    </button>
                  ))}
                  
                  {/* Batch Comparison option - only if compare mode */}
                  {compareMode && selectedBatchIds.length >= 2 && (
                    <button
                      onClick={() => toggleSection('batch_comparison')}
                      className={`w-full flex items-center justify-between p-3 rounded-sm border transition-colors ${
                        sections.batch_comparison
                          ? 'border-[#00E676]/30 bg-[#00E676]/5'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className="text-left">
                        <p className={sections.batch_comparison ? 'text-[#00E676]' : 'text-white/80'}>
                          Batch Comparison
                        </p>
                        <p className="text-white/40 text-xs">Side-by-side batch metrics</p>
                      </div>
                      <div className={`w-5 h-5 rounded-sm border flex items-center justify-center ${
                        sections.batch_comparison 
                          ? 'bg-[#00E676] border-[#00E676]' 
                          : 'border-white/30'
                      }`}>
                        {sections.batch_comparison && <Check size={12} weight="bold" className="text-black" />}
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 text-center">
              <div className="w-16 h-16 bg-[#00E676]/10 rounded-full flex items-center justify-center mx-auto">
                <Check size={32} className="text-[#00E676]" />
              </div>
              
              <div>
                <h3 className="text-white font-medium mb-2">Share Link Ready</h3>
                <p className="text-white/40 text-sm">
                  Anyone with this link can view your imported analytics report.
                </p>
              </div>
              
              {/* URL display */}
              <div className="bg-[#121212] border border-white/10 rounded-sm p-3">
                <div className="flex items-center gap-2">
                  <Link size={16} className="text-white/40 shrink-0" />
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="bg-transparent text-white/70 text-sm flex-1 outline-none"
                  />
                  <button
                    onClick={handleCopy}
                    className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                      copied 
                        ? 'bg-[#00E676] text-black' 
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {copied ? (
                      <span className="flex items-center gap-1">
                        <Check size={14} /> Copied
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Copy size={14} /> Copy
                      </span>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => window.open(shareUrl, '_blank')}
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                >
                  <Eye size={16} className="mr-2" />
                  Preview
                </Button>
                <Button
                  onClick={handleClose}
                  className="flex-1 bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer - only on step 1 */}
        {step === 1 && (
          <div className="flex justify-end gap-3 p-6 border-t border-white/10">
            <Button
              variant="outline"
              onClick={handleClose}
              className="border-white/10 text-white/70 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateShare}
              disabled={creating || !Object.values(sections).some(v => v)}
              className="bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90 disabled:opacity-50"
            >
              {creating ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Creating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Create Share Link
                  <ArrowRight size={16} />
                </span>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
